import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { SearchInput } from '@/components/ui/search-input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Pagination } from '@/components/ui/pagination';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Package, Plus } from 'lucide-react';
import { format, addDays } from 'date-fns';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { ViewModeToggle, type ViewMode } from '@/components/ui/view-mode-toggle';
import { assetsService, Asset } from '@/features/assets/services/assets.service';
import {
  RequestFormDialog,
  type RequestFormSubmitData,
} from '@/features/requests/components/RequestFormDialog';
import { EquipmentPhotoInput } from '@/features/requests/components/EquipmentPhotoInput';
import { assetKitsService } from '@/features/assets/services/asset-kits.service';
import { requestsService } from '@/features/requests/services/requests.service';

export const Route = createFileRoute('/_authenticated/equipments')({
  component: EquipmentsPage,
});

function EquipmentsPage() {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [search, setSearch] = useState('');
  const [locationFilter, setLocationFilter] = useState<string | undefined>(undefined);
  const [locationSearch, setLocationSearch] = useState('');
  const [availableFilter, setAvailableFilter] = useState<'available' | 'unavailable' | 'all'>(
    'available',
  );
  const [kitIdFilter, setKitIdFilter] = useState<string | undefined>(undefined);
  const [kitSearch, setKitSearch] = useState('');
  const [requestOpen, setRequestOpen] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null);
  const [requestKitOpen, setRequestKitOpen] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>('table');

  // Buscar assets públicos
  const { data: assetsData, isLoading } = useQuery({
    queryKey: ['assets-public', page, limit, search, locationFilter, availableFilter, kitIdFilter],
    queryFn: async () => {
      return await assetsService.getAllPublic({
        page,
        limit,
        search: search || undefined,
        location: locationFilter,
        availability: availableFilter,
        kitId: kitIdFilter,
      });
    },
  });

  const assets = assetsData?.items || [];
  const total = assetsData?.total || 0;

  // Obter localizações únicas dos assets
  const { data: allAssetsData } = useQuery({
    queryKey: ['assets-public-all'],
    queryFn: async () => {
      return await assetsService.getAllPublic({
        availability: 'all',
        limit: 1000, // Buscar muitos para ter todas as localizações
      });
    },
  });

  const { data: kits } = useQuery({
    queryKey: ['asset-kits-simple', kitSearch],
    queryFn: () => assetKitsService.getSimple(kitSearch || undefined),
  });

  const locations = useMemo(() => {
    const allAssets = allAssetsData?.items || [];
    const uniqueLocations = Array.from(
      new Set(allAssets.map((asset) => asset.location).filter(Boolean)),
    ).sort() as string[];
    return uniqueLocations;
  }, [allAssetsData]);

  // Mutation para criar solicitação (com a foto do equipamento)
  const createRequestMutation = useMutation({
    mutationFn: (data: RequestFormSubmitData) =>
      requestsService.create({
        assetId: data.assetId,
        type: 'withdrawal',
        withdrawalLocation: data.withdrawalLocation,
        plannedReturnDate: data.plannedReturnDate,
        termsAccepted: data.termsAccepted,
        quantityRequested: data.quantityRequested,
        photos: data.photos,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assets-public'] });
      queryClient.invalidateQueries({ queryKey: ['my-requests'] });
      setRequestOpen(false);
      setSelectedAsset(null);
    },
  });

  const createKitRequestMutation = useMutation({
    mutationFn: async (data: {
      kitId: string;
      withdrawalLocation: string;
      plannedReturnDate: string;
      termsAccepted: boolean;
      photos: File[];
    }) => requestsService.createKitRequest(data.kitId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assets-public'] });
      queryClient.invalidateQueries({ queryKey: ['my-requests'] });
      setRequestKitOpen(false);
    },
  });

  const openKitRequest = () => setRequestKitOpen(true);

  const handleRequest = (asset: Asset) => {
    if (asset.available && asset.available > 0) {
      setSelectedAsset(asset);
      setRequestOpen(true);
    }
  };

  // Sem equipamento escolhido: o diálogo abre na busca de equipamentos
  const handleNewRequest = () => {
    setSelectedAsset(null);
    setRequestOpen(true);
  };

  const handleSubmitRequest = async (data: RequestFormSubmitData) => {
    await createRequestMutation.mutateAsync(data);
  };

  const locationOptions = locations.filter((loc) =>
    loc.toLowerCase().includes(locationSearch.toLowerCase()),
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold">Equipamentos Disponíveis</h1>
          <p className="text-muted-foreground">Visualize e solicite equipamentos disponíveis</p>
        </div>
        <Button onClick={handleNewRequest} className="gap-2">
          <Plus className="h-4 w-4" />
          Solicitar equipamento
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Lista de Equipamentos</CardTitle>
        </CardHeader>
        <CardContent>
          {/* Filtros */}
          <div className="mb-4 flex flex-col gap-4 md:flex-row">
            <div className="flex-1">
              <SearchInput
                placeholder="Pesquisar por nome ou código..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            <Select
              value={availableFilter}
              onValueChange={(value) =>
                setAvailableFilter(value as 'available' | 'unavailable' | 'all')
              }
            >
              <SelectTrigger className="w-full md:w-[200px]">
                <SelectValue placeholder="Disponibilidade" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="available">Disponíveis</SelectItem>
                <SelectItem value="unavailable">Indisponíveis</SelectItem>
                <SelectItem value="all">Todos</SelectItem>
              </SelectContent>
            </Select>

            <SearchableSelect
              value={locationFilter}
              onValueChange={(value) => setLocationFilter(value === 'all' ? undefined : value)}
              searchValue={locationSearch}
              onSearchChange={setLocationSearch}
              searchPlaceholder="Buscar localização..."
              selectPlaceholder="Todas as localizações"
              allOptionLabel="Todas as localizações"
              options={locationOptions.map((location) => ({
                value: location,
                label: location,
              }))}
            />

            <SearchableSelect
              value={kitIdFilter}
              onValueChange={(value) => setKitIdFilter(value === 'all' ? undefined : value)}
              searchValue={kitSearch}
              onSearchChange={setKitSearch}
              searchPlaceholder="Buscar kit..."
              selectPlaceholder="Todos os kits"
              allOptionLabel="Todos os kits"
              options={(kits || []).map((kit) => ({
                value: kit.id,
                label: kit.name,
              }))}
            />

            <Button variant="outline" onClick={openKitRequest} className="gap-2">
              <Package className="h-4 w-4" />
              Solicitar Kit
            </Button>

            {(search || locationFilter || availableFilter !== 'available' || kitIdFilter) && (
              <Button
                variant="outline"
                onClick={() => {
                  setSearch('');
                  setLocationFilter(undefined);
                  setAvailableFilter('available');
                  setKitIdFilter(undefined);
                  setKitSearch('');
                }}
              >
                Limpar Filtros
              </Button>
            )}
            <ViewModeToggle value={viewMode} onChange={setViewMode} />
          </div>

          {/* Tabela */}
          {isLoading ? (
            <div className="py-8 text-center text-muted-foreground">Carregando...</div>
          ) : !assets || assets.length === 0 ? (
            <div className="py-8 text-center text-muted-foreground">
              Nenhum equipamento encontrado
            </div>
          ) : (
            <>
              {viewMode === 'table' ? (
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Código</TableHead>
                        <TableHead>Nome</TableHead>
                        <TableHead>Quantidade</TableHead>
                        <TableHead>Disponível</TableHead>
                        <TableHead>Localização</TableHead>
                        <TableHead className="text-right">Ações</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {assets.map((asset) => (
                        <TableRow key={asset.id}>
                          <TableCell className="font-medium">{asset.code || '-'}</TableCell>
                          <TableCell>{asset.name}</TableCell>
                          <TableCell>{asset.quantity}</TableCell>
                          <TableCell>{asset.available ?? '-'}</TableCell>
                          <TableCell>{asset.location || '-'}</TableCell>
                          <TableCell className="text-right">
                            {asset.available && asset.available > 0 ? (
                              <Button
                                size="sm"
                                onClick={() => handleRequest(asset)}
                                className="gap-2"
                              >
                                <Package className="h-4 w-4" />
                                Solicitar
                              </Button>
                            ) : (
                              <span className="text-sm text-muted-foreground">Indisponível</span>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                  {assets.map((asset) => (
                    <Card key={asset.id} className="overflow-hidden">
                      {asset.imageUrl ? (
                        <img
                          src={asset.imageUrl}
                          alt={asset.name}
                          className="h-40 w-full object-cover"
                        />
                      ) : (
                        <div className="h-40 w-full bg-muted flex items-center justify-center text-muted-foreground text-sm">
                          Sem imagem
                        </div>
                      )}
                      <CardHeader className="pb-2">
                        <CardTitle className="text-base">{asset.name}</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-1 text-sm">
                        <p>
                          <strong>Código:</strong> {asset.code || '-'}
                        </p>
                        <p>
                          <strong>Quantidade:</strong> {asset.quantity}
                        </p>
                        <p>
                          <strong>Disponível:</strong> {asset.available ?? '-'}
                        </p>
                        <p>
                          <strong>Localização:</strong> {asset.location || '-'}
                        </p>
                        <div className="pt-3">
                          {asset.available && asset.available > 0 ? (
                            <Button
                              size="sm"
                              onClick={() => handleRequest(asset)}
                              className="gap-2"
                            >
                              <Package className="h-4 w-4" />
                              Solicitar
                            </Button>
                          ) : (
                            <span className="text-sm text-muted-foreground">Indisponível</span>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}

              {/* Paginação */}
              {total > limit && (
                <div className="mt-4">
                  <Pagination total={total} page={page} limit={limit} onPageChange={setPage} />
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Modal de Solicitação */}
      <RequestFormDialog
        open={requestOpen}
        onOpenChange={setRequestOpen}
        asset={selectedAsset}
        onSubmit={handleSubmitRequest}
        isLoading={createRequestMutation.isPending}
      />

      <Dialog open={requestKitOpen} onOpenChange={setRequestKitOpen}>
        <DialogContent size="md" busy={createKitRequestMutation.isPending}>
          <DialogHeader>
            <DialogTitle>Solicitar kit</DialogTitle>
            <DialogDescription>
              Selecione um kit para solicitar todos os itens com as quantidades definidas nele.
            </DialogDescription>
          </DialogHeader>
          <KitRequestForm
            kits={kits || []}
            kitSearch={kitSearch}
            onKitSearchChange={setKitSearch}
            isLoading={createKitRequestMutation.isPending}
            onCancel={() => setRequestKitOpen(false)}
            onSubmit={(data) => createKitRequestMutation.mutate(data)}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Estado vive dentro do DialogContent: ao fechar o Radix desmonta e o modal reabre limpo.
export function KitRequestForm({
  kits,
  kitSearch,
  onKitSearchChange,
  isLoading,
  onCancel,
  onSubmit,
}: {
  kits: { id: string; name: string }[];
  kitSearch: string;
  onKitSearchChange: (v: string) => void;
  isLoading: boolean;
  onCancel: () => void;
  onSubmit: (data: {
    kitId: string;
    withdrawalLocation: string;
    plannedReturnDate: string;
    termsAccepted: boolean;
    photos: File[];
  }) => void;
}) {
  const [selectedKitId, setSelectedKitId] = useState<string | undefined>(undefined);
  const [returnDate, setReturnDate] = useState(format(addDays(new Date(), 7), 'yyyy-MM-dd'));
  const [photos, setPhotos] = useState<File[]>([]);

  return (
    <form
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        if (!selectedKitId || photos.length === 0) return;
        onSubmit({
          kitId: selectedKitId,
          withdrawalLocation: 'Solicitação de kit',
          plannedReturnDate: returnDate,
          termsAccepted: true,
          photos,
        });
      }}
    >
      <SearchInput
        placeholder="Pesquisar kit por nome..."
        value={kitSearch}
        onChange={(e) => onKitSearchChange(e.target.value)}
      />
      <Select value={selectedKitId} onValueChange={setSelectedKitId}>
        <SelectTrigger aria-label="Kit">
          <SelectValue placeholder="Selecione um kit" />
        </SelectTrigger>
        <SelectContent>
          {kits.map((kit) => (
            <SelectItem key={kit.id} value={kit.id}>
              {kit.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {selectedKitId && (
        <>
          <div className="space-y-2">
            <Label htmlFor="kitReturnDate">Dia da devolução *</Label>
            <Input
              id="kitReturnDate"
              type="date"
              min={format(new Date(), 'yyyy-MM-dd')}
              value={returnDate}
              onChange={(e) => setReturnDate(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label>Fotos dos equipamentos (1 a 5) *</Label>
            <EquipmentPhotoInput value={photos} onChange={setPhotos} disabled={isLoading} />
          </div>
        </>
      )}
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCancel} disabled={isLoading}>
          Cancelar
        </Button>
        <Button
          type="submit"
          loading={isLoading}
          disabled={!selectedKitId || !returnDate || photos.length === 0}
        >
          Solicitar kit
        </Button>
      </DialogFooter>
    </form>
  );
}
